import * as THREE from 'three';
import { formatDistance } from './format';

export type MarkerInput = {
  key: string;
  label: string;
  sub?: string;
  position: THREE.Vector3; // heliocentric
  radius: number;
  color: string;
  kind: 'body' | 'player' | 'moon';
  /** show label even when small/far */
  important: boolean;
};

type MarkerEl = { root: HTMLDivElement; label: HTMLSpanElement; dist: HTMLSpanElement; used: boolean };

const tmp = new THREE.Vector3();

/**
 * Projects world objects to screen: a dot + name + distance, a bracket around the current target,
 * and an edge arrow when the target is off screen.
 */
export class Markers {
  private readonly layer: HTMLDivElement;
  private readonly pool = new Map<string, MarkerEl>();
  private readonly arrow: HTMLDivElement;
  private readonly bracket: HTMLDivElement;

  constructor(parent: HTMLElement) {
    this.layer = document.createElement('div');
    this.layer.className = 'markers';
    parent.appendChild(this.layer);
    this.bracket = document.createElement('div');
    this.bracket.className = 'target-bracket';
    this.layer.appendChild(this.bracket);
    this.arrow = document.createElement('div');
    this.arrow.className = 'target-arrow';
    this.layer.appendChild(this.arrow);
  }

  private get(key: string): MarkerEl {
    let m = this.pool.get(key);
    if (!m) {
      const root = document.createElement('div');
      root.className = 'marker';
      const label = document.createElement('span');
      label.className = 'marker-label';
      const dist = document.createElement('span');
      dist.className = 'marker-dist';
      root.append(label, dist);
      this.layer.appendChild(root);
      m = { root, label, dist, used: false };
      this.pool.set(key, m);
    }
    return m;
  }

  update(
    items: MarkerInput[],
    origin: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
    targetKey: string | null,
    shipPos: THREE.Vector3,
    occluders: { position: THREE.Vector3; radius: number }[] = [],
  ): void {
    const w = window.innerWidth, h = window.innerHeight;
    for (const m of this.pool.values()) m.used = false;
    let targetShown = false;
    this.bracket.style.display = 'none';
    this.arrow.style.display = 'none';

    for (const it of items) {
      tmp.subVectors(it.position, origin);
      const dist = it.position.distanceTo(shipPos);
      const surfaceDist = Math.max(0, dist - it.radius);
      tmp.applyMatrix4(camera.matrixWorldInverse);
      const inFront = tmp.z < 0;
      tmp.applyMatrix4(camera.projectionMatrix);
      const sx = (tmp.x * 0.5 + 0.5) * w;
      const sy = (-tmp.y * 0.5 + 0.5) * h;
      const isTarget = it.key === targetKey;
      const onScreen = inFront && sx > -20 && sx < w + 20 && sy > -20 && sy < h + 20;
      // Apparent radius in px
      const apparent = inFront ? (it.radius / Math.max(dist, 1)) * (h / (2 * Math.tan((camera.fov * Math.PI) / 360))) : 0;

      if (isTarget) {
        targetShown = true;
        if (onScreen) {
          const size = Math.max(26, Math.min(apparent * 2 + 16, h * 0.8));
          this.bracket.style.display = 'block';
          this.bracket.style.transform = `translate(${sx - size / 2}px, ${sy - size / 2}px)`;
          this.bracket.style.width = this.bracket.style.height = `${size}px`;
        } else {
          // Edge arrow pointing towards the target.
          let dx = tmp.x, dy = -tmp.y;
          if (!inFront) { dx = -dx; dy = -dy; }
          const ang = Math.atan2(dy, dx);
          const r = Math.min(w, h) * 0.42;
          this.arrow.style.display = 'block';
          this.arrow.style.transform = `translate(${w / 2 + Math.cos(ang) * r}px, ${h / 2 + Math.sin(ang) * r}px) rotate(${ang}rad)`;
        }
      }

      if (onScreen && this.occluded(origin, it.position, it.radius, occluders)) continue;
      const bigOnScreen = apparent > 40;
      const showLabel = onScreen && (it.important || isTarget || apparent > 1.5) && !(bigOnScreen && !isTarget);
      if (!onScreen || (!showLabel && apparent > 3)) continue;
      const m = this.get(it.key);
      m.used = true;
      m.root.style.transform = `translate(${sx}px, ${sy}px)`;
      m.root.style.setProperty('--c', it.color);
      m.root.classList.toggle('is-target', isTarget);
      m.root.classList.toggle('is-player', it.kind === 'player');
      m.root.classList.toggle('no-label', !showLabel);
      if (m.label.textContent !== it.label) m.label.textContent = it.label;
      m.dist.textContent = showLabel ? formatDistance(surfaceDist) + (it.sub ? ` · ${it.sub}` : '') : '';
    }
    void targetShown;
    for (const m of this.pool.values()) m.root.style.display = m.used ? '' : 'none';
  }

  /** True if a sphere between the camera and the point hides it. */
  private occluded(origin: THREE.Vector3, p: THREE.Vector3, selfRadius: number, occ: { position: THREE.Vector3; radius: number }[]): boolean {
    const dir = new THREE.Vector3().subVectors(p, origin);
    const len = dir.length();
    dir.divideScalar(len);
    for (const o of occ) {
      if (o.position === p) continue;
      const oc = new THREE.Vector3().subVectors(o.position, origin);
      const t = oc.dot(dir);
      if (t <= 0 || t > len - selfRadius) continue;
      if (oc.lengthSq() - t * t < o.radius * o.radius) return true;
    }
    return false;
  }

  setVisible(v: boolean): void {
    this.layer.style.display = v ? '' : 'none';
  }
}
